import { defineStore } from 'pinia'
import type { CommunityMilestone } from '~/data/communityExpeditionData'

export interface CommunityExpeditionInfo {
  id: string
  weekKey: string
  legendarySlug: string
  totalCommunityDps: number
  startsAt: number
  endsAt: number
  milestones: CommunityMilestone[]
}

export interface UserContribution {
  totalDps: number
  sendsToday: number
  slotEndsAt: number | null
  claimedMilestones: number[]
  maxSendsPerDay: number
  slotDuration: number
}

export interface LeaderboardEntry {
  rank: number
  userId: number
  username: string
  totalDps: number
  isCurrentUser?: boolean
}

export interface CommunityExpeditionState {
  expedition: CommunityExpeditionInfo
  contribution: UserContribution
  leaderboard: LeaderboardEntry[]
  userRank: number | null
}

function normalizeClaimedMilestones(raw: unknown): number[] {
  if (!Array.isArray(raw)) return []
  const ids: number[] = []
  for (const value of raw) {
    const id = Math.floor(Number(value))
    if (Number.isFinite(id) && id > 0) ids.push(id)
  }
  return ids
}

export const useCommunityExpeditionStore = defineStore('communityExpedition', () => {
  const expedition = ref<CommunityExpeditionInfo | null>(null)
  const contribution = ref<UserContribution | null>(null)
  const leaderboard = ref<LeaderboardEntry[]>([])
  const userRank = ref<number | null>(null)

  // How many milestones are claimable right now (community reached + personal met + not yet claimed)
  const claimableCount = computed(() => {
    if (!expedition.value || !contribution.value) return 0
    return expedition.value.milestones.filter((m) => {
      if (contribution.value!.claimedMilestones.includes(m.id)) return false
      if (expedition.value!.totalCommunityDps < m.communityThreshold) return false
      if (contribution.value!.totalDps < m.personalMinDps) return false
      return true
    }).length
  })

  const slotAvailable = computed(() => {
    if (!contribution.value) return true
    if (!contribution.value.slotEndsAt) return true
    return Math.floor(Date.now() / 1000) >= contribution.value.slotEndsAt
  })

  const canSendToday = computed(() => {
    if (!contribution.value) return true
    return contribution.value.sendsToday < contribution.value.maxSendsPerDay
  })

  function clearFromRunReset() {
    expedition.value = null
    contribution.value = null
    leaderboard.value = []
    userRank.value = null
  }

  function setFromLoad(data: CommunityExpeditionState) {
    expedition.value = {
      ...data.expedition,
      totalCommunityDps: Number(data.expedition.totalCommunityDps) || 0,
    }
    contribution.value = {
      ...data.contribution,
      totalDps: Number(data.contribution.totalDps) || 0,
      sendsToday: Number(data.contribution.sendsToday) || 0,
      maxSendsPerDay: Number(data.contribution.maxSendsPerDay) || 0,
      slotDuration: Number(data.contribution.slotDuration) || 0,
      slotEndsAt: data.contribution.slotEndsAt == null ? null : Number(data.contribution.slotEndsAt),
      claimedMilestones: normalizeClaimedMilestones(data.contribution.claimedMilestones),
    }
    leaderboard.value = data.leaderboard.map((entry) => ({
      ...entry,
      totalDps: Number(entry.totalDps) || 0,
    }))
    userRank.value = data.userRank
  }

  function updateAfterSend(slotEndsAt: number, teamDps: number) {
    if (!contribution.value) return
    const sentDps = Number(teamDps) || 0
    contribution.value.slotEndsAt = Number(slotEndsAt) || null
    contribution.value.sendsToday = (Number(contribution.value.sendsToday) || 0) + 1
    contribution.value.totalDps = (Number(contribution.value.totalDps) || 0) + sentDps
    if (expedition.value) {
      expedition.value.totalCommunityDps = (Number(expedition.value.totalCommunityDps) || 0) + sentDps
    }
  }

  function markMilestoneClaimed(milestoneId: number) {
    if (!contribution.value) return
    if (!contribution.value.claimedMilestones.includes(milestoneId)) {
      contribution.value.claimedMilestones = [...contribution.value.claimedMilestones, milestoneId]
    }
  }

  return {
    expedition,
    contribution,
    leaderboard,
    userRank,
    claimableCount,
    slotAvailable,
    canSendToday,
    setFromLoad,
    clearFromRunReset,
    updateAfterSend,
    markMilestoneClaimed,
  }
})
